import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/languageContext';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

interface LanguageSelectorProps {
    variant?: 'header' | 'page';
    className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
    variant = 'header',
    className = ''
}) => {
    const { t, i18n } = useTranslation();
    const { currentLanguage, changeLanguage } = useLanguage();

    const languages = [
        { code: 'en', name: t('language.en'), flag: '🇺🇸' },
        { code: 'es', name: t('language.es'), flag: '🇪🇸' },
        { code: 'fr', name: t('language.fr'), flag: '🇫🇷' },
        { code: 'de', name: t('language.de'), flag: '🇩🇪' },
        { code: 'tr', name: t('language.tr'), flag: '🇹🇷' },
        { code: 'nl', name: t('language.nl'), flag: '🇳🇱' },
    ];

    // Ensure we always have a valid language code
    const effectiveLanguage = currentLanguage || i18n.language || 'en';

    const handleLanguageChange = (value: string) => {
        changeLanguage(value);
    };

    const selectedLang = languages.find(lang => lang.code === effectiveLanguage) || languages[0];

    const isHeaderVariant = variant === 'header';
    const triggerClasses = isHeaderVariant
        ? "w-12 h-9 bg-white/10 hover:bg-white/20 text-white border-none rounded-full px-2"
        : "w-auto h-9 bg-gray-100 hover:bg-gray-200 text-gray-800 border-gray-300 rounded-lg px-3";

    return (
        <Select value={effectiveLanguage} onValueChange={handleLanguageChange}>
            <SelectTrigger className={`${triggerClasses} ${className}`}>
                <SelectValue>
                    {isHeaderVariant ? (
                        <span className="text-base">{selectedLang.flag}</span>
                    ) : (
                        <div className="flex items-center gap-2">
                            <span className="text-base">{selectedLang.flag}</span>
                            <span className="font-medium text-sm">{selectedLang.name}</span>
                        </div>
                    )}
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                {languages.map((language) => (
                    <SelectItem key={language.code} value={language.code}>
                        <div className="flex items-center gap-2">
                            <span className="text-base">{language.flag}</span>
                            <span className="font-medium">{language.name}</span>
                        </div>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

export default LanguageSelector;
